import { cn } from "@/lib/utils";
import { Sparkles } from "lucide-react";
import { MarkdownRenderer } from "@/components/ui/MarkdownRenderer";
import { ChatMessage } from "@/types/chat";
import { format, isToday, isThisYear } from "date-fns";
import { memo, useMemo } from "react";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { ReasoningBlock } from "./ReasoningBlock";
import { ToolCallBlock } from "./ToolCallBlock";
import { StatusPill } from "./StatusPill";

const HIDDEN_TOOL_NAMES = new Set(["exec", "read", "write", "bash"]);

interface MessageBubbleProps {
    message: ChatMessage;
    showTimestamp?: boolean;
    isStreaming?: boolean;
    /** Optional: Override user info for admin review (shows employee instead of current user) */
    employeeUser?: { fullName: string; avatarUrl?: string };
}

/**
 * MessageBubble Component
 * Displays a single chat message with avatar, content, and optional timestamp
 * 
 * @param message - Chat message to display
 * @param showTimestamp - Whether to show relative timestamp (default: true)
 * @param isStreaming - Whether the message is currently being streamed
 * @param employeeUser - Optional user info for admin review mode (overrides useAuth)
 */
export const MessageBubble = memo(({
    message,
    showTimestamp = true,
    isStreaming = false,
    employeeUser,
}: MessageBubbleProps) => {
    const isUser = message.role === "user";
    const { user: authUser } = useAuth();
    
    // Use employeeUser if provided (admin review mode), otherwise use current auth user
    const displayUser = employeeUser || authUser;

    const formattedTime = useMemo(() => {
        if (!message.createdAt) return "";
        const date = new Date(message.createdAt);

        if (isToday(date)) {
            return format(date, "p"); // e.g. 1:45 PM
        }

        if (isThisYear(date)) {
            return format(date, "MMM d, p"); // e.g. Dec 31, 1:45 PM
        }

        return format(date, "MMM d, yyyy, p"); // e.g. Dec 31, 2024, 1:45 PM
    }, [message.createdAt]);

    return (
        <div
            className={cn("flex gap-4 group animate-in fade-in slide-in-from-bottom-2", isUser && "justify-end")}
        >
            {/* Assistant Avatar */}
            {!isUser && (
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-primary shadow-sm mt-1">
                    <Sparkles className="h-4 w-4 text-primary-foreground" />
                </div>
            )}

            {/* Message Content Container */}
            <div className="flex flex-col gap-1 max-w-[85%]">
                {/* Message Bubble */}
                <div
                    className={cn(
                        "relative rounded-2xl px-5 py-3.5 shadow-sm transition-all",
                        isUser
                            ? "bg-primary text-primary-foreground rounded-tr-sm ml-12"
                            : "bg-muted/50 border border-border/50 rounded-tl-sm mr-12",
                        isStreaming && !isUser && "border-primary/30 shadow-glow-sm animate-pulse-subtle"
                    )}
                >
                    <div className="relative">
                        {!isUser && message.customStatuses?.map((s) => (
                            <StatusPill key={`${s.name}-${s.startedAt}`} status={s} />
                        ))}
                        {!isUser && message.reasoning && (
                            <ReasoningBlock
                                key={isStreaming ? 'streaming' : 'done'}
                                content={message.reasoning}
                                defaultOpen={isStreaming}
                            />
                        )}
                        {!isUser && message.toolCalls?.map((tc) => {
                            const shouldHide = !isStreaming && HIDDEN_TOOL_NAMES.has(tc.toolName.toLowerCase());
                            return (
                                <div
                                    key={tc.toolCallId}
                                    className={cn(
                                        "transition-all duration-300 overflow-hidden",
                                        shouldHide ? "opacity-0 max-h-0" : "opacity-100 max-h-96"
                                    )}
                                >
                                    <ToolCallBlock call={tc} />
                                </div>
                            );
                        })}
                        <MarkdownRenderer
                            content={message.content}
                            className={isUser ? "prose-invert" : ""}
                        />
                        {isStreaming && !isUser && (
                            <span className="inline-block w-1.5 h-4 ml-1 bg-primary align-middle animate-cursor-blink" />
                        )}
                    </div>
                </div>

                {/* Timestamp */}
                {showTimestamp && message.createdAt && (
                    <span
                        className={cn(
                            "text-[10px] text-muted-foreground/60 px-2 mt-0.5",
                            isUser ? "text-right" : "text-left"
                        )}
                    >
                        {formattedTime}
                    </span>
                )}
            </div>

            {/* User Avatar */}
            {isUser && (
                <UserAvatar
                    src={displayUser?.avatarUrl}
                    name={displayUser?.fullName}
                    className="h-8 w-8 flex-shrink-0 border border-primary/20 shadow-sm mt-1"
                />
            )}
        </div>
    );
});

MessageBubble.displayName = "MessageBubble";
